import { Check } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import type { BuildStage, StageStatus } from './buildStages';

/** Read out after each step, so its state is never carried by colour alone. */
const STATUS_TEXT: Record<StageStatus, string> = {
  done: 'Done',
  active: 'In progress',
  pending: 'Not started',
};

/**
 * The build's three steps, left to right, joined by a line that fills as each
 * one finishes. Under each label: "Done", or how long the active step usually
 * takes. Steps run strictly in order, so only one ever spins.
 */
export function BuildStepper({ stages }: { stages: BuildStage[] }) {
  return (
    <ol aria-label='Build steps' className='flex w-full items-start'>
      {stages.map((stage, i) => (
        <li
          key={stage.key}
          data-stage={stage.key}
          data-status={stage.status}
          aria-current={stage.status === 'active' ? 'step' : undefined}
          className='relative flex min-w-0 flex-1 flex-col items-center text-center'
        >
          {i > 0 && (
            <span
              aria-hidden='true'
              className={cn(
                'absolute right-1/2 top-3 h-0.5 w-full -translate-y-1/2 rounded-full',
                stage.status === 'pending' ? 'bg-border' : 'bg-primary/60'
              )}
            />
          )}
          <StepMarker status={stage.status} index={i + 1} />
          <span
            className={cn(
              'mt-2 px-1 text-sm leading-snug',
              stage.status === 'pending'
                ? 'text-muted-foreground'
                : 'font-medium text-foreground'
            )}
          >
            {stage.label}
            <span className='sr-only'>, {STATUS_TEXT[stage.status]}</span>
          </span>
          <span
            aria-hidden='true'
            className='mt-0.5 font-mono text-[11px] tabular-nums text-muted-foreground'
          >
            {stage.status === 'done'
              ? 'Done'
              : stage.status === 'active'
                ? stage.estimate
                : ' '}
          </span>
        </li>
      ))}
    </ol>
  );
}

function StepMarker({ status, index }: { status: StageStatus; index: number }) {
  // On the card's surface so the connector line stops at its edge.
  const base =
    'relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ring-4 ring-card';
  if (status === 'done') {
    return (
      <span aria-hidden='true' className={cn(base, 'bg-primary')}>
        <Check
          className='h-3.5 w-3.5 text-primary-foreground'
          strokeWidth={3}
        />
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span
        aria-hidden='true'
        className={cn(base, 'border-2 border-primary/25 bg-card')}
      >
        <span className='absolute inset-[-2px] animate-spin rounded-full border-2 border-transparent border-t-primary motion-reduce:animate-none' />
        <span className='h-2 w-2 rounded-full bg-primary' />
      </span>
    );
  }
  return (
    <span
      aria-hidden='true'
      className={cn(
        base,
        'border border-border bg-card font-mono text-[11px] text-muted-foreground'
      )}
    >
      {index}
    </span>
  );
}
